import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { whatsappAPI } from './whatsappAPI';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Bot, Save, Loader2, Layers, Tag, Clock, MessageSquareText, PenLine, Users, Workflow, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatWa } from './waFormat';
import SetoresConfig from './config/SetoresConfig';
import EtiquetasConfig from './config/EtiquetasConfig';
import HorarioConfig from './config/HorarioConfig';
import RegrasConfig from './config/RegrasConfig';
import EquipeConfig from './config/EquipeConfig';
import IaConfig from './config/IaConfig';
import FluxoConfig, { textoMenu, saudacaoTexto } from './config/FluxoConfig';
import { recarregarCfgAtendimento } from './organizacao';

/* Configurações do atendimento WhatsApp (rota /whatsapp/chatbot — mantida pelas permissões já gravadas). */

const ABAS = [
  ['assistente', 'Assistente', Bot],
  ['fluxo', 'Fluxo', Workflow],
  ['regras', 'Palavras-chave', MessageSquareText],
  ['setores', 'Setores', Layers],
  ['etiquetas', 'Etiquetas', Tag],
  ['horario', 'Horário', Clock],
  ['equipe', 'Equipe', Users],
  ['ia', 'IA', Sparkles],
];
const COM_SALVAR = ['assistente', 'fluxo', 'horario', 'equipe', 'ia'];

function Toggle({ checked, onChange, label, desc, testid }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
        className={`mt-0.5 w-10 h-6 rounded-full shrink-0 relative transition-colors ${checked ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-600'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </button>
      <span>
        <span className="text-sm font-medium block">{label}</span>
        {desc && <span className="text-xs text-muted-foreground">{desc}</span>}
      </span>
    </label>
  );
}

function assinar(cfg, nome, setor, texto) {
  if (!cfg.assinar_mensagens) return texto;
  const primeiro = (nome.split(' ')[0] || '');
  let a = (cfg.assinatura_formato || '*{nome}:*').replaceAll('{nome}', nome).replaceAll('{primeiro_nome}', primeiro).replaceAll('{setor}', setor);
  a = a.replaceAll('**', '').replace(/[\s·|–\-,/]+$/, '').trim() || `*${nome}:*`;
  return cfg.assinatura_posicao === 'rodape' ? `${texto}\n\n${a}` : `${a}\n${texto}`;
}

export default function ChatbotWA() {
  const { isAdmin } = useAuth();
  const admin = typeof isAdmin === 'function' ? isAdmin() : !!isAdmin;
  const [params, setParams] = useSearchParams();
  const aba = ABAS.some(([k]) => k === params.get('aba')) ? params.get('aba') : 'assistente';
  const [cfg, setCfg] = useState(null);
  const [alterado, setAlterado] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    whatsappAPI.config().then(r => setCfg(r.data)).catch(() => toast.error('Não foi possível carregar a configuração'));
  }, []);

  const up = (k) => (v) => { setAlterado(true); setCfg(c => ({ ...c, [k]: v?.target ? v.target.value : v })); };
  const salvar = async () => {
    setSalvando(true);
    try {
      const { horarios_resumo: _r, ...body } = cfg;
      const r = await whatsappAPI.salvarConfig(body); setCfg(r.data); setAlterado(false); toast.success('Configuração salva');
      recarregarCfgAtendimento();   // lista/quadro do atendimento passam a usar na hora
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar'); }
    finally { setSalvando(false); }
  };
  const irPara = (k) => {
    if (alterado && COM_SALVAR.includes(aba) && !COM_SALVAR.includes(k) && !window.confirm('Há alterações não salvas. Sair mesmo assim?')) return;
    setParams({ aba: k }, { replace: true });
  };

  if (!cfg) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  const menuPreview = `${saudacaoTexto(cfg.fluxo, 'João', cfg.nome_empresa || '', cfg.bot_nome || '')}\n\n${textoMenu(cfg.fluxo, 'inicio', false)}`;

  return (
    <div className="space-y-4" data-testid="wa-chatbot-page">
      <div className="flex items-center gap-2 flex-wrap justify-between sticky top-0 z-20 -mx-1 px-1 py-1 bg-background/90 backdrop-blur">
        <div className="flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 overflow-x-auto max-w-full">
          {ABAS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => irPara(k)} data-testid={`wa-cfg-aba-${k}`}
              className={`h-8 px-3 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${aba === k ? 'bg-white dark:bg-slate-900 shadow-sm text-emerald-700 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}>
              <Icon className="w-4 h-4" />{l}
            </button>
          ))}
        </div>
        {admin && COM_SALVAR.includes(aba) && (
          <Button onClick={salvar} disabled={salvando || !alterado} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-cfg-salvar">
            {salvando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} {alterado ? 'Salvar alterações' : 'Salvo'}
          </Button>
        )}
      </div>
      {!admin && <p className="text-xs text-amber-700">Somente administradores podem alterar estas opções.</p>}

      {aba === 'assistente' && (
        <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
          <div className="space-y-4">
            <Card className="p-5">
              <h3 className="font-semibold flex items-center gap-2 mb-2"><Bot className="w-5 h-5 text-emerald-600" /> Assistente</h3>
              <fieldset disabled={!admin} className="divide-y">
                <Toggle checked={cfg.bot_ativo} onChange={up('bot_ativo')} testid="wa-cfg-bot"
                  label="Assistente ativo"
                  desc="Recebe o cliente, informa o andamento do projeto pelo CPF e faz o cadastro. Desligado: toda conversa nova vai direto para a fila." />
                <div className="py-3">
                  <label className="text-sm font-medium" htmlFor="wa-cfg-bot-nome">Nome do assistente nas mensagens</label>
                  <Input id="wa-cfg-bot-nome" value={cfg.bot_nome ?? ''} onChange={up('bot_nome')} placeholder="Equipe AgrolinkBR" maxLength={60} className="mt-1 max-w-sm" data-testid="wa-cfg-bot-nome" />
                  <p className="text-xs text-muted-foreground mt-1">Aparece como remetente das mensagens automáticas no atendimento e no lugar de <code>{'{assistente}'}</code> na saudação.</p>
                </div>
                <div className="py-3">
                  <label className="text-sm font-medium">Nome da empresa (aparece na saudação)</label>
                  <Input value={cfg.nome_empresa || ''} onChange={up('nome_empresa')} placeholder="Ex.: AgroLink Projetos" className="mt-1 max-w-sm" />
                </div>
                <Toggle checked={cfg.pesquisa_satisfacao} onChange={up('pesquisa_satisfacao')} testid="wa-cfg-avaliacao"
                  label="Pedir avaliação do atendimento ao finalizar"
                  desc="Pede uma nota de 1 a 5, registrada por atendente. Desligado: não pede. Ao finalizar, quem finaliza ainda pode marcar ou desmarcar só daquela vez." />
                <Toggle checked={cfg.mostrar_pendencias} onChange={up('mostrar_pendencias')}
                  label="Listar as pendências do projeto para o cliente"
                  desc="Desligado: o assistente só avisa que há pendências e sugere falar com um atendente (recomendado se as pendências têm anotações internas)." />
                <Toggle checked={cfg.pedir_telefone_oculto !== false} onChange={up('pedir_telefone_oculto')} testid="wa-cfg-pedir-tel"
                  label="Perguntar o número quando o WhatsApp esconder"
                  desc="Alguns contatos chegam sem número (privacidade do WhatsApp). No primeiro contato, o assistente pede o número com DDD, uma vez só; dá para pular. O número fica marcado como informado, e não vincula cliente sozinho." />
                {cfg.pedir_telefone_oculto !== false && (
                  <div className="pb-3 pl-[52px]">
                    <label className="text-sm font-medium" htmlFor="wa-cfg-msg-tel">Mensagem</label>
                    <Textarea id="wa-cfg-msg-tel" value={cfg.msg_pedir_telefone || ''} onChange={up('msg_pedir_telefone')} rows={3} maxLength={500}
                      className="mt-1 text-sm" data-testid="wa-cfg-msg-tel" />
                    <p className="text-xs text-muted-foreground mt-1">Vai depois de “Olá! 👋 Aqui é a *{cfg.bot_nome || 'Equipe AgrolinkBR'}*.”</p>
                  </div>
                )}
              </fieldset>
            </Card>

            <Card className="p-5">
              <h3 className="font-semibold flex items-center gap-2 mb-2"><PenLine className="w-5 h-5 text-emerald-600" /> Assinatura das mensagens</h3>
              <fieldset disabled={!admin} className="space-y-3">
                <Toggle checked={cfg.assinar_mensagens} onChange={up('assinar_mensagens')} testid="wa-cfg-assinar"
                  label="Assinar as mensagens com o nome do atendente"
                  desc="O cliente também é avisado quando alguém assume ou transfere o atendimento." />
                {cfg.assinar_mensagens && (
                  <div className="pl-[52px] space-y-3">
                    <label className="block text-sm font-medium">Formato
                      <Input value={cfg.assinatura_formato || ''} onChange={up('assinatura_formato')} className="mt-1 max-w-sm font-mono text-sm" data-testid="wa-cfg-assinatura" />
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {['*{nome}:*', '*{nome}* · {setor}', '— {primeiro_nome} ({setor})', '_{nome}_'].map(f => (
                        <button key={f} type="button" onClick={() => up('assinatura_formato')(f)}
                          className="text-xs font-mono px-2 py-1 rounded-md border hover:bg-slate-50 dark:hover:bg-slate-800">{f}</button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <code>{'{nome}'}</code> nome completo · <code>{'{primeiro_nome}'}</code> · <code>{'{setor}'}</code> setor da conversa (some se não houver).
                      Use *asteriscos* para negrito e _sublinhado_ para itálico, como no WhatsApp.
                    </p>
                    <div className="flex gap-2">
                      {[['topo', 'Antes da mensagem'], ['rodape', 'No fim da mensagem']].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => up('assinatura_posicao')(k)} data-testid={`wa-cfg-pos-${k}`}
                          className={`h-8 px-3 rounded-full text-sm border ${cfg.assinatura_posicao === k ? 'bg-emerald-600 border-emerald-600 text-white' : ''}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </fieldset>
            </Card>
          </div>

          <Card className="p-4 lg:sticky lg:top-16">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Como o cliente vê</p>
            <div className="rounded-xl p-3 space-y-2" style={{ background: '#efeae2' }}>
              <div className="bg-white rounded-lg px-3 py-2 text-[13px] w-fit text-slate-900">oi, bom dia</div>
              <div className="bg-[#d9fdd3] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap ml-auto max-w-[92%] text-slate-900">{formatWa(menuPreview)}</div>
              <div className="bg-white rounded-lg px-3 py-2 text-[13px] w-fit text-slate-900">2</div>
              <div className="bg-[#d9fdd3] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap ml-auto max-w-[92%] text-slate-900" data-testid="wa-preview-assinatura">
                {formatWa(assinar(cfg, 'Maria Souza', 'Financeiro', 'Bom dia, João! Já vou verificar seu boleto.'))}
              </div>
            </div>
            <ul className="text-xs text-muted-foreground mt-3 space-y-1 list-disc pl-4">
              <li>Status do projeto só é informado se o CPF for de um cliente com <b>este mesmo telefone</b> no cadastro.</li>
              <li>Com 2 ou mais setores no menu, a opção 2 pergunta o setor (aba <b>Setores</b>).</li>
              <li>O cliente pode digitar <b>menu</b> ou <b>atendente</b> a qualquer momento.</li>
            </ul>
          </Card>
        </div>
      )}
      {aba === 'regras' && <RegrasConfig admin={admin} />}
      {aba === 'setores' && <SetoresConfig admin={admin} />}
      {aba === 'etiquetas' && <EtiquetasConfig admin={admin} />}
      {aba === 'horario' && <HorarioConfig cfg={cfg} up={up} admin={admin} />}
      {aba === 'equipe' && <EquipeConfig cfg={cfg} up={up} admin={admin} />}
      {aba === 'fluxo' && <FluxoConfig cfg={cfg} up={up} admin={admin} />}
      {aba === 'ia' && <IaConfig cfg={cfg} up={up} admin={admin} />}
    </div>
  );
}
